import { ChevronLeft } from "@untitledui/icons";
import { X } from "@untitledui/icons/X";
import type { ReactNode } from "react";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Button } from "@/components/base/buttons/button";
import { cx } from "@/utils/cx";

export const PageLayout = ({
  className,
  isWide = false,
  children,
}: {
  className?: string;
  /** Whether the page spreads to desktop width instead of the phone column. */
  isWide?: boolean;
  children: ReactNode;
}) => {
  return (
    <div className={cx("relative h-dvh w-dvw text-primary overflow-hidden", className)}>
      <main className={cx("relative bg-primary flex flex-col mx-auto size-full overflow-hidden", isWide ? "max-w-screen-xl" : "max-w-screen-sm")}>
        {children}
      </main>
    </div>
  );
};

export const PageLoading = () => {
  return (
    <div className="flex items-center justify-center min-h-dvh min-w-dvw">
      <LoadingIndicator />
    </div>
  );
};

export const PageProgressBar = ({
  current,
  total,
  className,
}: {
  current: number;
  total: number;
  className?: string;
}) => {
  return (
    <div
      className={cx(
        "flex flex-row items-center justify-center gap-0.5 w-full",
        className,
      )}
    >
      {Array.from({ length: total }, (_, index) => {
        const stepNumber = index + 1;
        const isComplete = index < current;
        const isLast = current > total && index === total - 1;
        const isCurrent = index === current || isLast;

        return (
          <div
            key={stepNumber}
            className="flex items-center flex-grow group mt-4 relative"
          >
            {index < total && (
              <div
                className={cx(
                  "h-2.5 mx-0.25 flex-grow group-first-of-type:rounded-l-sm group-last-of-type:rounded-r-sm",
                  isComplete ? "bg-brand-section" : "bg-gray-300",
                )}
              />
            )}
            {isCurrent && (
              <div
                className={cx(
                  "h-7 w-7 rounded-full flex items-center justify-center absolute",
                  "bg-secondary border-white border-3 z-1",
                  isLast ? "right-[-12px]" : "left-[-12px]",
                )}
              >
                <svg
                  className="w-4 h-4 text-brand-secondary"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <title>Completed step</title>
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const PageTitle = ({
  title,
  className,
}: {
  title: string;
  className?: string;
}) => {
  return (
    <h1
      className={cx("text-xl text-primary text-left font-semibold", className)}
    >
      {title}
    </h1>
  );
};

export const PageSubtitle = ({
  subtitle,
  className,
}: {
  subtitle: string;
  className?: string;
}) => {
  return <p className={cx("text-lg text-quaternary", className)}>{subtitle}</p>;
};

export const PageTitleSection = ({
  title,
  subtitle,
  className,
}: {
  title: string;
  subtitle?: string;
  className?: string;
}) => {
  return (
    <div className={cx("flex flex-col gap-4", className)}>
      <PageTitle title={title} />
      {subtitle ? <PageSubtitle subtitle={subtitle} /> : null}
    </div>
  );
};

export const PageHeaderBackButton = ({
  onBack,
  children,
}: {
  onBack: () => void;
  children?: ReactNode;
}) => {
  return (
    <Button
      color="link-gray"
      onClick={onBack}
      iconLeading={<ChevronLeft className="size-7" />}
    >
      {children}
    </Button>
  );
};

export const PageHeaderCloseButton = ({ onClose }: { onClose: () => void }) => {
  return (
    <Button
      color="link-gray"
      onClick={onClose}
      iconLeading={<X className="size-7" />}
    />
  );
};

export const PageHeader = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <header
      className={cx(
        "flex flex-row items-center justify-between w-full px-4 py-2 shadow-[0px_1px_7px_-5px_rgba(0,0,0,1)] z-10",
        className,
      )}
    >
      {children}
    </header>
  );
};

export const PageSubheader = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return <div className={cx("bg-white p-4", className)}>{children}</div>;
};

export const PageContentContainer = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cx(
        `relative flex flex-col flex-1 overflow-y-auto w-full px-6 py-4 bg-gray-50`,
        className,
      )}
    >
      {children}
    </div>
  );
};
